<!--
// app/components/pages/region-tables/TableStandingRegularPhase.vue
-->
<script setup lang="ts">
import type {
  StandingPlayoffColors,
  StandingPositionColor,
} from "~/src/season-cluster/domain/models/01.Season";
import type { Phase } from "~/src/season-cluster/domain/models/02.Phase";
import { setAliasUrl, slugify } from "~/src/Shared/utils";
import BaseBackdropLoading from "~/app/components/base/BaseBackdropLoading.vue";
import StandingsLegend from "~/app/components/Shared/standings/StandingsLegend.vue";
import seasonService from "~/src/season-cluster/domain/services/seasonService";

type Props = {
  positionLegend: StandingPlayoffColors[] | StandingPlayoffColors[][] | undefined;
  secondaryPositionLegend: StandingPlayoffColors[] | StandingPlayoffColors[][] | undefined;
  seasonId: number | undefined;
  loading: boolean;
  standingPhase: Phase | undefined;
};

const props = defineProps<Props>();

const localePath = useLocalePath();
const { getPositionLegendForGroup } = seasonService();

const getRowColor = (teamIndex: number, groupIndex: number): string | undefined => {
  const colors = getPositionLegendForGroup(props.positionLegend, groupIndex);
  const playoffPosition = colors ? colors[teamIndex] : undefined;
  return getRowPositionColorTailwindClass(playoffPosition?.color);
};

const getSecondaryRowColor = (teamIndex: number, groupIndex: number): string | undefined => {
  const colors = getPositionLegendForGroup(props.secondaryPositionLegend, groupIndex);
  const secondaryPlayoffPosition = colors ? colors[teamIndex] : undefined;
  return getSecondaryRowPositionColorTailwindClass(secondaryPlayoffPosition?.color);
};

const flattenedPositionLegend = computed((): StandingPlayoffColors[] | undefined => {
  if (!props.positionLegend) return undefined;
  if (Array.isArray(props.positionLegend[0])) {
    return (props.positionLegend as StandingPlayoffColors[][]).flat();
  }
  return props.positionLegend as StandingPlayoffColors[];
});

const flattenedSecondaryPositionLegend = computed((): StandingPlayoffColors[] | undefined => {
  if (!props.secondaryPositionLegend) return undefined;
  if (Array.isArray(props.secondaryPositionLegend[0])) {
    return (props.secondaryPositionLegend as StandingPlayoffColors[][]).flat();
  }
  return props.secondaryPositionLegend as StandingPlayoffColors[];
});

const isKingsFirstSplit = computed(() => {
  return props.seasonId === 3;
});

function getRowPositionColorTailwindClass(playoffColor: StandingPositionColor | undefined) {
  //This function MUST be defined in the Vue file. In any other cases, Tailwind won't render the classes.
  switch (playoffColor) {
    case "green":
      return "!bg-[#10E217]";
    case "orange":
      return "!bg-amber-400";
    case "softOrange":
      return "!bg-[#EC7B39]";
    case "darkOrange":
      return "!bg-[#FD5900]";
    case "red":
      return "!bg-red-500";
    case "yellow":
      return "!bg-[#FFFB00]";
    case "brown":
      return "!bg-[#A8834E]";
    case "gray":
      return "!bg-gray-500";
    case "greenDashed":
      return "!bg-[repeating-linear-gradient(to_bottom,#10E217_0,#10E217_8px,_transparent_8px,_transparent_12px)]";
    case "yellowDashed":
      return "!bg-[repeating-linear-gradient(to_bottom,#FFFB00_0,#FFFB00_8px,_transparent_8px,_transparent_12px)]";
    default:
      return "!bg-gray-800";
  }
}

function getSecondaryRowPositionColorTailwindClass(playoffColor: StandingPositionColor | undefined) {
  //This function MUST be defined in the Vue file. In any other cases, Tailwind won't render the classes.
  switch (playoffColor) {
    case "green":
      return "before:!bg-[#10E217]";
    case "orange":
      return "before:!bg-amber-400";
    case "softOrange":
      return "before:!bg-[#EC7B39]";
    case "darkOrange":
      return "before:!bg-[#FD5900]";
    case "red":
      return "before:!bg-red-500";
    case "yellow":
      return "before:!bg-[#FFFB00]";
    case "brown":
      return "before:!bg-[#A8834E]";
    case "gray":
      return "before:!bg-gray-500";
    case "greenDashed":
      return "before:!bg-[repeating-linear-gradient(to_bottom,#10E217_0,#10E217_8px,_transparent_8px,_transparent_12px)]";
    case "yellowDashed":
      return "before:!bg-[repeating-linear-gradient(to_bottom,#FFFB00_0,#FFFB00_8px,_transparent_8px,_transparent_12px)]";
    default:
      return "before:!bg-gray-800";
  }
}

const isChallengerCompetition = computed(() => {
  return props.standingPhase?.groups.some(g => g.name?.toLowerCase() === "challenger");
});

// Computed to get results of challengers
const challengerData = computed(() => {
  const teamResults = new Map<number, boolean | undefined>();
  const groupWins = new Map<number, number>();

  if (!isChallengerCompetition.value || !props.standingPhase) {
    return { teamResults, groupWins };
  }

  // Create map teamId → groupId to know which group each team belongs to
  const teamToGroup = new Map<number, number>();

  props.standingPhase.groups.forEach(group => {
    if (group.hasStandings && group.standings) {
      group.standings.forEach(standing => {
        teamToGroup.set(standing.team.id, group.id);
      });
      // Initialize wins counter for this group
      groupWins.set(group.id, 0);
    }
  });

  // Search Challenger group
  const challengerGroup = props.standingPhase.groups.find(g => g.name?.toLowerCase() === "challenger");

  if (!challengerGroup?.turns) {
    return { teamResults, groupWins };
  }

  // Iterate in turns and matches
  for (const turn of challengerGroup.turns) {
    for (const match of turn.matches || []) {
      // Only get ended matches
      if (match.status !== "ended") continue;

      const { homeTeamId, awayTeamId } = match.participants;
      const { homeScore, awayScore, homeScoreP, awayScoreP } = match.scores;

      // Get final scores (with penalties if any)
      const finalHomeScore = homeScoreP !== null ? homeScoreP : homeScore;
      const finalAwayScore = awayScoreP !== null ? awayScoreP : awayScore;

      // Set match winner
      const homeWon = finalHomeScore > finalAwayScore;
      const awayWon = finalAwayScore > finalHomeScore;

      // Save individual results by team
      teamResults.set(homeTeamId, homeWon);
      teamResults.set(awayTeamId, awayWon);

      // Count victories by group based on actual team group membership
      if (homeWon) {
        const homeTeamGroupId = teamToGroup.get(homeTeamId);
        if (homeTeamGroupId !== undefined) {
          groupWins.set(homeTeamGroupId, (groupWins.get(homeTeamGroupId) || 0) + 1);
        }
      } else if (awayWon) {
        const awayTeamGroupId = teamToGroup.get(awayTeamId);
        if (awayTeamGroupId !== undefined) {
          groupWins.set(awayTeamGroupId, (groupWins.get(awayTeamGroupId) || 0) + 1);
        }
      }
    }
  }

  return { teamResults, groupWins };
});
</script>

<template>
  <div v-if="standingPhase" class="relative">
    <div v-if="seasonId !== 1 && seasonId !== 8" class="pb-2">
      <p class="kql-page-section-title">Regular</p>
      <hr />
    </div>

    <base-backdrop-loading :active="loading" />

    <div
      v-for="(groupStanding, groupIndex) in standingPhase.groups.filter(g => g.hasStandings)"
      :key="groupStanding.id"
    >
      <div
        :class="[
          'flex text-gray-400 uppercase text-sm mb-2',
          isChallengerCompetition ? 'justify-between' : 'justify-end md:justify-between',
        ]"
      >
        <span v-if="standingPhase.groups.length > 1" class="standing-group-title"
          >{{ $t("general.group.word") }}
          <span class="standing-group-name">{{ groupStanding.name }}</span></span
        >
        <span v-if="isChallengerCompetition" class="font-bold text-primary">
          Challengers:
          <span class="text-white">{{ challengerData.groupWins.get(groupStanding.id) || 0 }}</span>
        </span>
      </div>
      <hr v-if="standingPhase.groups.length > 1" />
      <div
        id="standingDesktop"
        class="bg-gray-900 p-4 rounded-lg my-3 relative overflow-x-scroll hide-scrollbar"
      >
        <div
          class="container-standing-rows overflow-x-scroll hide-scrollbar"
          :class="groupIndex === 0 && standingPhase.groups.length > 1 ? 'mb-4' : 'mb-0'"
        >
          <div class="header-table-standing">
            <div class="header-standing-team">
              <span class="text-left rtl:text-right">{{ $t("tables.position.ab") }}</span>
              <span class="col-start-2 col-span-3 ps-4">{{ $t("tables.team.word") }}</span>
            </div>
            <div :class="['header-standing-data', { 'extra-card': isChallengerCompetition }]">
              <span v-if="!isKingsFirstSplit" class="header-title-item">{{ $t("tables.points.ab") }}</span>
              <span class="header-title-item">{{ $t("tables.gamesPlayed.ab") }}</span>
              <span :class="['header-title-item', { 'order-first': isKingsFirstSplit }]">
                {{ $t("tables.wins.ab") }}
              </span>
              <span class="header-title-item">{{ $t("tables.penaltyWins.ab") }}</span>
              <span class="header-title-item">{{ $t("tables.penaltyLoses.ab") }}</span>
              <span class="header-title-item">{{ $t("tables.lost.ab") }}</span>
              <span class="header-title-item">{{ $t("tables.goalsPro.ab") }}</span>
              <span class="header-title-item">{{ $t("tables.goalsAgainst.ab") }}</span>
              <span class="header-title-item">{{ $t("tables.goalsDifference.ab") }}</span>
              <span v-if="isChallengerCompetition" class="header-title-item">sc</span>
            </div>
          </div>
          <div
            v-for="(standingTeam, teamIndex) in groupStanding.standings"
            :key="standingTeam.id"
            class="standing-row relative min-w-max"
          >
            <div
              :class="[
                'standing-row-left',
                { 'secondary-standing': !!secondaryPositionLegend },
                getSecondaryRowColor(teamIndex, groupIndex),
              ]"
            >
              <span :class="['split-standing', getRowColor(teamIndex, groupIndex)]" />
              <div class="standing-row-rank-team">
                <div class="standing-rank">
                  <p>{{ standingTeam.rank }}</p>
                </div>

                <div class="standing-team-container">
                  <div class="logo-team-container">
                    <NuxtImg
                      :src="setAliasUrl(standingTeam.team.logo.url)"
                      :alt="standingTeam.team.name"
                      class="object-contain"
                      :loading="teamIndex > 6 ? 'lazy' : 'eager'"
                      width="44"
                      height="44"
                    />
                  </div>
                  <div class="hidden md:block leading-tight">
                    <nuxt-link
                      :to="
                        localePath({
                          name: 'teams-team-teamName',
                          params: {
                            team: standingTeam.team.id,
                            teamName: slugify(standingTeam.team.name),
                          },
                        })
                      "
                    >
                      <h2>{{ standingTeam.team.name }}</h2>
                    </nuxt-link>
                  </div>
                  <div class="block md:hidden">
                    <nuxt-link
                      :to="
                        localePath({
                          name: 'teams-team-teamName',
                          params: {
                            team: standingTeam.team.id,
                            teamName: slugify(standingTeam.team.name),
                          },
                        })
                      "
                    >
                      {{ standingTeam.team.shortName }}
                    </nuxt-link>
                  </div>
                </div>
              </div>
            </div>

            <div :class="['standing-row-data', { 'extra-card': isChallengerCompetition }]">
              <span v-if="!isKingsFirstSplit" class="standing-table-item-data !text-white">
                {{ standingTeam.points }}
              </span>
              <span class="standing-table-item-data">{{ standingTeam.gameTotal }}</span>
              <span :class="['standing-table-item-data', { 'order-first': isKingsFirstSplit }]">
                {{ standingTeam.gameWon }}
              </span>
              <span class="standing-table-item-data">{{ standingTeam.gamePenaltyWon }}</span>
              <span class="standing-table-item-data">{{ standingTeam.gamePenaltyLost }}</span>
              <span class="standing-table-item-data">{{ standingTeam.gameLost }}</span>
              <span class="standing-table-item-data">{{ standingTeam.goalPro }}</span>
              <span class="standing-table-item-data">{{ standingTeam.goalAgainst }}</span>
              <span class="standing-table-item-data">
                {{ standingTeam.goalPro - standingTeam.goalAgainst }}
              </span>
              <span v-if="isChallengerCompetition" class="standing-table-item-data">
                <img
                  v-if="
                    challengerData.teamResults.get(standingTeam.team.id) === true ||
                    challengerData.teamResults.get(standingTeam.team.id) === false
                  "
                  :src="`/img/secret-card/sc-${challengerData.teamResults.get(standingTeam.team.id)}.png`"
                  alt="secret card icon"
                  width="20"
                />
                <img
                  v-else
                  src="/img/secret-card/default.png"
                  alt="secret card icon"
                  width="20"
                  class="max-w-5 h-auto object-contain"
                />
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-if="flattenedPositionLegend" class="mt-4">
    <standings-legend :legend="flattenedPositionLegend" large />
  </div>
  <div v-if="flattenedSecondaryPositionLegend" class="mt-4">
    <standings-legend :legend="flattenedSecondaryPositionLegend" large />
  </div>
</template>

<style scoped>
.standing-group-title {
  @apply text-primary flex justify-center items-center text-[10px] gap-0.5;
}

.standing-group-name {
  @apply px-1.5 rounded border border-primary uppercase text-[10px] font-normal text-primary leading-[17px] ms-0.5;
}

.header-table-standing {
  @apply flex justify-start items-stretch text-xs text-gray-400 uppercase mb-2 min-w-max;
}

.standing-row {
  @apply flex justify-start items-center relative bg-gray-800 h-16 mb-1 rounded-lg;
}

.standing-row-left {
  @apply rounded-s-lg overflow-hidden bg-gray-800 sticky ltr:left-0 rtl:right-0 w-[170px] md:w-auto h-16 flex items-center;
}

.standing-row-data {
  @apply rounded-e-lg overflow-hidden bg-gray-800 h-16;
}

.standing-row-rank-team {
  @apply grid grid-cols-[30px_1fr_1fr_1fr] md:grid-cols-4 ps-3 md:ps-4 md:text-lg font-bold w-[160px] md:w-[362px] md:min-w-[362px] md:max-w-[450px];
}

.header-standing-team {
  @apply grid grid-cols-[30px_1fr_1fr_1fr] md:grid-cols-4 ps-3 md:ps-4 w-[170px] md:w-[362px] md:min-w-[362px] md:max-w-[450px] sticky ltr:left-0 rtl:right-0 bg-gray-900;
}

.standing-rank {
  @apply h-full w-full flex items-center justify-start text-left;
}

.secondary-standing {
  @apply before:min-w-[4px] before:h-full before:bg-gray-800 before:me-0.5;
}

span.split-standing {
  @apply h-16 w-[4px] bg-gray-800;
}

.standing-team-container {
  @apply col-start-2 col-span-3 text-left flex justify-start gap-3 md:gap-7 items-center h-full;
}

.logo-team-container {
  @apply w-11 h-11 flex justify-center items-center shrink-0;
}

.standing-table-item-data {
  @apply text-center h-full flex justify-center items-center md:text-lg text-gray-400 font-bold;
}

.header-title-item {
  @apply justify-center flex;
}

.header-standing-data,
.standing-row-data {
  @apply grid grid-cols-[repeat(9,20px)] gap-x-7 ms-auto text-center pe-5 min-w-max;
}

.header-standing-data.extra-card,
.standing-row-data.extra-card {
  @apply !grid-cols-[repeat(10,20px)];
}
</style>
